<template>
  <div id="pageReporte6">
    <v-container fluid>
      <v-row>
        <v-col>
          <v-card class="elevation-2">
            <v-toolbar color="primary" dark flat class="report-toolbar">
              <v-icon class="mr-2">mdi-truck-delivery-outline</v-icon>
              <v-toolbar-title>Vehículos pendientes de llegada</v-toolbar-title>
              <v-spacer></v-spacer>
              <report-filters :fields="['location_id', 'condition', 'created_by', 'review_threshold']" @change="onFilterChange"></report-filters>
              <v-btn outlined :loading="loadingExport" @click="downloadExcel">
                <v-icon left small>mdi-microsoft-excel</v-icon>
                <span class="d-none d-sm-inline">Descargar Excel</span>
              </v-btn>
              <v-btn outlined class="ml-2" :loading="loadingPdf" @click="downloadPdf(6)">
                <v-icon left small>mdi-file-pdf-box</v-icon>
                <span class="d-none d-sm-inline">Descargar PDF</span>
              </v-btn>
            </v-toolbar>

            <v-card-text class="pt-6">
              <div class="text-body-2 grey--text text--darken-1 mb-6">
                Vehículos que llevan demasiado tiempo Sin bastidor o Sin ubicación. Un vehículo Sin ubicación muy antiguo puede
                ser uno que ya ha llegado y que nadie ha recepcionado en la herramienta.
              </div>

              <v-progress-linear v-if="loadingPreview" indeterminate color="primary" class="mb-6"></v-progress-linear>

              <div v-else>
                <v-alert v-if="!previewItems.length" type="info" text dense>
                  No hay datos para mostrar
                </v-alert>

                <template v-else>
                  <!-- KPIs -->
                  <div class="kpi-grid mb-8">
                    <div class="kpi-card">
                      <v-icon size="30" color="blue darken-2">mdi-barcode-off</v-icon>
                      <div class="kpi-number">{{ kpis.sinBastidor }}</div>
                      <div class="kpi-label">Sin bastidor</div>
                    </div>
                    <div class="kpi-card">
                      <v-icon size="30" color="teal darken-1">mdi-map-marker-off-outline</v-icon>
                      <div class="kpi-number">{{ kpis.sinUbicacion }}</div>
                      <div class="kpi-label">Sin ubicación</div>
                    </div>
                    <div class="kpi-card">
                      <v-icon size="30" color="red darken-1">mdi-alert-circle-outline</v-icon>
                      <div class="kpi-number">{{ kpis.aRevisar }}</div>
                      <div class="kpi-label">A revisar</div>
                      <div class="kpi-subtitle">{{ reviewThreshold }} días o más desde la creación</div>
                    </div>
                    <div class="kpi-card">
                      <v-icon size="30" color="purple darken-1">mdi-timer-outline</v-icon>
                      <div class="kpi-number">{{ kpis.mediaDias }} d</div>
                      <div class="kpi-label">Media de días desde la creación</div>
                    </div>
                  </div>

                  <!-- Días desde la creación -->
                  <div class="report-section-title">
                    <v-icon small color="primary" class="mr-1">mdi-timer-sand</v-icon>
                    Días desde la creación
                  </div>
                  <v-simple-table class="report-table mb-8" dense>
                    <thead>
                      <tr>
                        <th>Estado</th>
                        <th v-for="tramo in tramos" :key="tramo.key" class="text-center">{{ tramo.label }}</th>
                        <th class="text-center">Total</th>
                        <th class="text-center">A revisar</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="row in byStatus" :key="row.status">
                        <td>{{ row.status }}</td>
                        <td v-for="(tramo, index) in tramos" :key="tramo.key" class="text-center">
                          <span v-if="row.tramos[tramo.key] > 0" class="bucket-pill" :class="`bucket-${index}`">{{ row.tramos[tramo.key] }}</span>
                          <span v-else class="grey--text">0</span>
                        </td>
                        <td class="text-center font-weight-bold">{{ row.total }}</td>
                        <td class="text-center">
                          <span v-if="row.aRevisar > 0" class="bucket-pill bucket-3">{{ row.aRevisar }}</span>
                          <span v-else>0</span>
                        </td>
                      </tr>
                    </tbody>
                  </v-simple-table>

                  <!-- Por usuario creador -->
                  <div class="report-section-title">
                    <v-icon small color="primary" class="mr-1">mdi-account-outline</v-icon>
                    Por usuario creador
                  </div>
                  <v-alert v-if="onlyUnregisteredCreator" type="info" text dense border="left" class="mb-3">
                    El usuario creador solo se guarda en los vehículos nuevos. Los anteriores aparecen como "Sin registrar".
                  </v-alert>
                  <v-simple-table class="report-table mb-8" dense>
                    <thead>
                      <tr>
                        <th>Usuario</th>
                        <th class="text-center">Sin bastidor</th>
                        <th class="text-center">Sin ubicación</th>
                        <th class="text-center">Total</th>
                        <th class="text-center">A revisar</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="row in byCreator" :key="row.creator">
                        <td class="font-weight-bold">{{ row.creator }}</td>
                        <td class="text-center">{{ row.sinBastidor }}</td>
                        <td class="text-center">{{ row.sinUbicacion }}</td>
                        <td class="text-center font-weight-bold">{{ row.total }}</td>
                        <td class="text-center">
                          <span v-if="row.aRevisar > 0" class="bucket-pill bucket-3">{{ row.aRevisar }}</span>
                          <span v-else>0</span>
                        </td>
                      </tr>
                    </tbody>
                  </v-simple-table>

                  <!-- Detalle -->
                  <div class="report-section-title">
                    <v-icon small color="primary" class="mr-1">mdi-format-list-bulleted</v-icon>
                    Detalle de vehículos a revisar
                  </div>
                  <v-alert v-if="!detailRows.length" type="success" text dense>
                    Ningún vehículo lleva {{ reviewThreshold }} días o más pendiente
                  </v-alert>
                  <v-data-table
                    v-else
                    :headers="detailHeaders"
                    :items="detailRows"
                    class="report-table mb-4"
                    dense
                    item-key="id"
                    sort-by="days_since_creation"
                    sort-desc
                    :footer-props="{ itemsPerPageOptions: [10, 25, 50] }"
                    :items-per-page="10"
                  >
                    <template v-slot:item.vehicle="{ item }">
                      <div class="font-weight-medium">{{ item.vehicle }}</div>
                      <div class="text-caption grey--text">Matrícula: {{ item.plate || '—' }}<template v-if="item.iic"> · IIC {{ item.iic }}</template></div>
                      <div class="text-caption grey--text">Bastidor: {{ item.frame || '—' }}</div>
                    </template>
                    <template v-slot:item.status="{ item }">
                      <v-chip small outlined :color="item.status === 'Sin bastidor' ? 'blue darken-2' : 'teal darken-1'">{{ item.status }}</v-chip>
                    </template>
                    <template v-slot:item.created_at="{ item }">
                      {{ formatDateOnly(item.created_at) }}
                    </template>
                    <template v-slot:item.days_since_creation="{ item }">
                      <span class="bucket-pill" :class="tramoClass(item.tramo)">{{ item.days_since_creation }}</span>
                    </template>
                  </v-data-table>
                </template>
              </div>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>
    </v-container>
  </div>
</template>

<script>
import moment from 'moment'
import ReportService from '../../services/ReportService'
import errorHandler from '../../mixins/errorHandler'
import reportPdf from '../../mixins/reportPdf'
import ReportFilters from '../../components/ReportFilters'

const SIN_BASTIDOR = 'Sin bastidor'
const SIN_UBICACION = 'Sin ubicación'
const SIN_REGISTRAR = 'Sin registrar'
// Valor por defecto del backend (review_threshold); solo se usa para los textos
const REVIEW_THRESHOLD = 30

// Los guiones de los tramos son en-dash, tal y como los devuelve el backend
const TRAMOS = [
  { key: '0–15', label: '0–15 días' },
  { key: '16–30', label: '16–30 días' },
  { key: '31–60', label: '31–60 días' },
  { key: 'Más de 60', label: '>60 días' }
]

const countBy = (rows, predicate) => rows.filter(predicate).length

export default {
  name: 'Reporte6',

  components: { ReportFilters },

  mixins: [errorHandler, reportPdf],

  data: () => ({
    params: {},
    previewItems: [],
    loadingPreview: true,
    loadingExport: false,
    reviewThreshold: REVIEW_THRESHOLD,
    tramos: TRAMOS,

    detailHeaders: [
      { text: 'Vehículo', value: 'vehicle' },
      { text: 'Condición', value: 'condition' },
      { text: 'Estado', value: 'status' },
      { text: 'Creado', value: 'created_at' },
      { text: 'Días', value: 'days_since_creation' },
      { text: 'Creado por', value: 'created_by' }
    ]
  }),

  computed: {
    kpis () {
      const rows = this.previewItems
      const days = rows.map(row => row.days_since_creation)
      const media = days.length ? days.reduce((a, b) => a + b, 0) / days.length : null

      return {
        sinBastidor: countBy(rows, row => row.status === SIN_BASTIDOR),
        sinUbicacion: countBy(rows, row => row.status === SIN_UBICACION),
        aRevisar: countBy(rows, row => row.review),
        mediaDias: media !== null ? media.toFixed(1).replace('.', ',') : '—'
      }
    },

    byStatus () {
      return [SIN_BASTIDOR, SIN_UBICACION].map(status => {
        const rows = this.previewItems.filter(row => row.status === status)
        const tramos = {}
        TRAMOS.forEach(tramo => { tramos[tramo.key] = countBy(rows, row => row.tramo === tramo.key) })

        return {
          status,
          tramos,
          total: rows.length,
          aRevisar: countBy(rows, row => row.review)
        }
      })
    },

    byCreator () {
      const groups = {}
      this.previewItems.forEach(row => {
        const creator = row.created_by || SIN_REGISTRAR
        if (!groups[creator]) groups[creator] = []
        groups[creator].push(row)
      })

      return Object.keys(groups).map(creator => {
        const rows = groups[creator]
        return {
          creator,
          sinBastidor: countBy(rows, row => row.status === SIN_BASTIDOR),
          sinUbicacion: countBy(rows, row => row.status === SIN_UBICACION),
          total: rows.length,
          aRevisar: countBy(rows, row => row.review)
        }
      }).sort((a, b) => b.total - a.total)
    },

    onlyUnregisteredCreator () {
      return this.byCreator.length === 1 && this.byCreator[0].creator === SIN_REGISTRAR
    },

    detailRows () {
      return this.previewItems.filter(row => row.review)
    }
  },

  methods: {
    onFilterChange (params) {
      this.params = params
      this.loadPreview()
    },

    tramoClass (tramo) {
      const index = TRAMOS.findIndex(item => item.key === tramo)
      return index >= 0 ? `bucket-${index}` : ''
    },

    formatDateOnly (value) {
      if (!value) return '—'
      return moment.unix(value).format('DD/MM/YYYY')
    },

    async loadPreview () {
      try {
        this.loadingPreview = true
        const { data } = await ReportService.getReport6(this.params)
        this.previewItems = data.data || []
      } catch (error) {
        this.handleApiError(error, 'Error al cargar los datos del reporte')
      } finally {
        this.loadingPreview = false
      }
    },

    async downloadExcel () {
      try {
        this.loadingExport = true
        const response = await ReportService.downloadReport6(this.params)
        const blob = response.data

        if (!blob || blob.size === 0) {
          throw new Error('El archivo descargado está vacío')
        }

        const url = window.URL.createObjectURL(blob)
        const link = document.createElement('a')
        link.href = url
        const timestamp = new Date().toISOString().slice(0, 19).replace(/[:T]/g, '-')
        link.download = `reporte6_${timestamp}.xlsx`
        link.style.display = 'none'

        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        window.URL.revokeObjectURL(url)

        this.$toast.success('Reporte descargado correctamente')
      } catch (error) {
        this.handleApiError(error, 'Error al descargar el reporte')
      } finally {
        this.loadingExport = false
      }
    }
  },

  created () {
    this.loadPreview()
  }
}
</script>

<style scoped>
.kpi-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(160px, 1fr));
  gap: 16px;
}

.kpi-card {
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  padding: 20px 12px;
  text-align: center;
}

.kpi-number {
  font-size: 30px;
  font-weight: 600;
  line-height: 1.3;
  margin-top: 4px;
}

.kpi-label {
  font-size: 14px;
  color: rgba(0, 0, 0, 0.7);
}

.kpi-subtitle {
  font-size: 12px;
  color: rgba(0, 0, 0, 0.5);
}

.report-section-title {
  display: flex;
  align-items: center;
  font-size: 16px;
  font-weight: 500;
  margin-bottom: 12px;
}

.report-table {
  border: 1px solid #e0e0e0;
  border-radius: 8px;
}

.report-table >>> thead th {
  background-color: #fafafa;
  font-weight: 600 !important;
}

.bucket-pill {
  display: inline-block;
  min-width: 28px;
  padding: 2px 8px;
  border-radius: 12px;
  font-weight: 500;
  text-align: center;
}

.bucket-0 {
  background-color: #E8F5E9;
  color: #2E7D32;
}

.bucket-1 {
  background-color: #FFF8E1;
  color: #F9A825;
}

.bucket-2 {
  background-color: #FFF3E0;
  color: #EF6C00;
}

.bucket-3 {
  background-color: #FFEBEE;
  color: #C62828;
}
</style>
